import { IconX } from '@developerz/ui';
import { createEffect, type JSX, on, onMount, Show } from 'solid-js';
import { t } from '../../i18n';
import { absolute } from '../../lib/time';

// One pane's search bar (#4401, epic #4391): Ctrl/⌘+F or the title bar's
// Search button opens it. A PRESENTER. `TerminalPane.tsx` owns the query, the
// matches and the current match (`lib/terminal/search.ts` finds them); this
// owns the input, the count and the keys.
//
// KEYS, inside the input only: Enter = next match, Shift+Enter = previous,
// Escape = close (the pane moves focus back to its own log). The count line is
// `role="status"`, so "3 of 12" is announced as it changes without stealing
// focus from the input.
//
// WHERE IT STOPPED. The pane searches only the rows it holds (the ring
// buffer's 5,000, narrowed by its filter), so the bar names the oldest line it
// read. "No matches" never implies nothing older matches.
export function TerminalPaneSearch(props: {
  readonly id: string;
  readonly query: string;
  readonly onQueryChange: (query: string) => void;
  /** 1-based position of the current match, or null when there is none. */
  readonly current: number | null;
  readonly total: number;
  /** The oldest row the pane searched — `null` when it holds none. */
  readonly searchedBackTo: string | null;
  readonly onStep: (direction: 1 | -1) => void;
  readonly onClose: () => void;
  /** Bumped to re-focus the input when Ctrl/⌘+F lands on an already-open bar. */
  readonly focusRequest: () => number;
}): JSX.Element {
  let inputRef: HTMLInputElement | undefined;
  onMount(() => inputRef?.focus());
  createEffect(
    on(props.focusRequest, () => inputRef?.select(), {
      defer: true,
    }),
  );

  // Keyed on `total`, not `current`: a match can exist with no current one
  // (it arrived after the query, or the current one was evicted), and saying
  // "No matches" then would contradict the highlighted lines.
  const count = (): string => {
    if (props.query.trim() === '') return '';
    if (props.total === 0) return t('terminal.search_none');
    return props.current === null
      ? t('terminal.search_total', { total: props.total })
      : t('terminal.search_count', { current: props.current, total: props.total });
  };

  function onKeyDown(e: KeyboardEvent): void {
    if (e.key === 'Enter') {
      e.preventDefault();
      props.onStep(e.shiftKey ? -1 : 1);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      props.onClose();
    }
  }

  return (
    // biome-ignore lint/a11y/useSemanticElements: Solid's JSX types have no `<search>` element yet; the landmark role is the same contract.
    <div class="terminal__pane-search" role="search">
      <label class="sr-only" for={`terminal-pane-search-${props.id}`}>
        {t('terminal.search_label')}
      </label>
      <input
        id={`terminal-pane-search-${props.id}`}
        ref={inputRef}
        type="search"
        class="terminal__pane-search-input"
        placeholder={t('terminal.search_placeholder')}
        autocomplete="off"
        spellcheck={false}
        value={props.query}
        onInput={(e) => props.onQueryChange(e.currentTarget.value)}
        onKeyDown={onKeyDown}
      />
      <span class="terminal__pane-search-count" role="status">
        {count()}
      </span>
      <button
        type="button"
        class="terminal__pane-action"
        disabled={props.total === 0}
        aria-label={t('terminal.search_prev')}
        title={t('terminal.search_prev')}
        onClick={() => props.onStep(-1)}
      >
        <span aria-hidden="true">↑</span>
      </button>
      <button
        type="button"
        class="terminal__pane-action"
        disabled={props.total === 0}
        aria-label={t('terminal.search_next')}
        title={t('terminal.search_next')}
        onClick={() => props.onStep(1)}
      >
        <span aria-hidden="true">↓</span>
      </button>
      <button
        type="button"
        class="terminal__pane-action"
        aria-label={t('terminal.search_close')}
        title={t('terminal.search_close')}
        onClick={() => props.onClose()}
      >
        <IconX size="0.75rem" />
      </button>
      <Show when={props.query.trim() !== '' ? props.searchedBackTo : null}>
        {(ts) => (
          <span class="terminal__pane-search-scope">
            {t('terminal.search_scope', { ts: absolute(ts()) })}
          </span>
        )}
      </Show>
    </div>
  );
}
